import {
  useEffect, useMemo, useRef, useState
} from 'react'
import {
  Stack, Tooltip, useMediaQuery, useTheme
} from '@mui/material'
import { Tag } from 'bloomer'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useHistory } from 'react-router-dom'
import InfoIcon from '@mui/icons-material/Info'
import { setNextInspectionsSort } from '../../../actions/sortActions'
import api from '../../../api'
import { NextInspectionDto } from '../../../api/types/checklist'
import { IAlphaprocessState } from '../../../store'
import DateUtil from '../../../util/DateUtil'
import LoadSpinner from '../../loadSpinner/LoadSpinner'
import DataTable from '../../widgets/DataTable'
import { IDataTableHeader } from '../../widgets/DataTableHeader'
import { TableDueDate } from './TableDueDate'

import useNewDesign from '../../../hooks/useNewDesign'
import ScheduledListV2 from './ScheduledListV2'
import PlannedProcessDialog from './PlannedProcessDialog'

const PER_PAGE = 15

export default function ScheduledChecklistsTable() {
  const { t } = useTranslation()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const history = useHistory()
  const dispatch = useDispatch()
  const newDesign = useNewDesign()

  const sortSettings = useSelector(
    (state: IAlphaprocessState) => state.sort.nextInspectionsSort
  )
  const filters = useSelector(
    (state: IAlphaprocessState) => state.filter.checklistFilterSettings
  )

  const [data, setData] = useState<NextInspectionDto[]>([])
  const [mobileItems, setMobileItems] = useState<NextInspectionDto[]>([])
  const [isLoading, setIsLoading] = useState(true)
  // the planned process whose schedule is shown (click on its title)
  const [opened, setOpened] = useState<NextInspectionDto | null>(null)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(0)

  const pageRef = useRef(page)
  useEffect(() => { pageRef.current = page }, [page])

  const sentinelRef = useRef<HTMLDivElement>(null)
  const loadingMoreRef = useRef(false)

  // ── Server-side fetch (fires on every page / filter / sort change) ──────────
  useEffect(() => {
    setIsLoading(true)
    api.schedules.nextInspections({
      page,
      sort: sortSettings.column || 'start_date',
      direction: sortSettings.direction || 'asc',
      title: filters.term || undefined,
      project_id: filters.selectedProjects.length
        ? filters.selectedProjects.map((x: any) => x.value)
        : undefined,
      start_date: filters.startDate || undefined,
      end_date: filters.endDate || undefined,
    })
      .then((result) => {
        setData(result.data)
        setTotalPages(result.totalPages)
      })
      .catch(console.error)
      .finally(() => setIsLoading(false))
  }, [page, sortSettings, filters])

  // ── Accumulate pages on mobile (reads pageRef to avoid stale closure) ───────
  useEffect(() => {
    if (!isMobile) return
    if (pageRef.current === 1) {
      setMobileItems(data)
    } else {
      setMobileItems((prev) => [...prev, ...data])
    }
    loadingMoreRef.current = false
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  // ── Reset page + mobile list when the base query changes ────────────────────
  const queryKey = useMemo(() => JSON.stringify({
    sort: sortSettings,
    term: filters.term,
    projects: filters.selectedProjects.map((x: any) => x.value),
    startDate: filters.startDate,
    endDate: filters.endDate,
  }), [sortSettings, filters])

  const prevQueryRef = useRef<string | null>(null)
  useEffect(() => {
    if (prevQueryRef.current !== null && prevQueryRef.current !== queryKey) {
      setPage(1)
      setMobileItems([])
    }
    prevQueryRef.current = queryKey
  }, [queryKey])

  // ── Infinite-scroll sentinel (mobile only) ──────────────────────────────────
  useEffect(() => {
    if (!isMobile || !sentinelRef.current) return
    const el = sentinelRef.current
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (
          entry.isIntersecting
          && !isLoading
          && !loadingMoreRef.current
          && page < totalPages
        ) {
          loadingMoreRef.current = true
          setPage((prev) => prev + 1)
        }
      },
      { rootMargin: '200px' }
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [isMobile, isLoading, page, totalPages])

  const handleLatestResultClick = async (scheduleId: number) => {
    try {
      const response = await api.schedules.latestResultSet(scheduleId)
      if (response.data && response.data.id) {
        const { checklist_id, id } = response.data
        history.push(`/checklists/${checklist_id}/audit/${id}`)
      } else {
        alert('No latest result set available for this schedule.')
      }
    } catch (error) {
      console.error('Error fetching latest result set:', error)
      alert('An error occurred while fetching the latest result set.')
    }
  }

  // When the old backend returns all data unpaginated (no Total-Count header),
  // paginateResponse gives totalPages=0.  Slice client-side so the table doesn't
  // render hundreds of rows at once and hang the browser.
  const desktopTotalPages = totalPages || Math.ceil(data.length / PER_PAGE)
  const desktopData = totalPages === 0
    ? data.slice((page - 1) * PER_PAGE, page * PER_PAGE)
    : data

  const displayData = isMobile ? mobileItems : desktopData

  // starts a process from the schedule (same link for the old table and the new list)
  const startLink = (x: NextInspectionDto) => `/resultset/${x.checklist_id}/new?due_date=${encodeURI(x.due_date as any)}`
    + `&project_id=${x.project_id}&tester_id=${x.user_ids.join(',')}&title=${
      encodeURI(x.title)}&team_ids=${x.team_ids.join(',')}&location=${
      x.location}&contact_id=${x.contact_id}&resource_id=${x.ressource_id}`
    + `&schedule_id=${x.schedule_id}`

  const tableData = displayData.map((x) => {
    const titleCell = (
      <div style={{ width: '100%' }}>
        <div>
          <Link
            className={clsx(
              'has-hyphens',
              'is-inline-flex-mobile',
              x.non_working_day_excluded && 'has-text-strike-through'
            )}
            style={isMobile ? { whiteSpace: 'normal', wordBreak: 'break-word', display: 'block' } : undefined}
            to={startLink(x)}
            onClick={(e) => {
              e.preventDefault()
              setOpened(x)
            }}
          >
            {x.title}
            {x.rescheduled_due_to_non_working_day && (
              <Tooltip title={t('schedule.rescheduledTooltip')}>
                <InfoIcon sx={{ height: '0.75em', color: 'orange' }} />
              </Tooltip>
            )}
          </Link>
          {x.non_working_day_excluded && (
            <Tooltip title={t('schedule.nonWorkingDayTooltip')}>
              <InfoIcon sx={{ height: '0.75em' }} />
            </Tooltip>
          )}
          {x.autostart && (
            <Tag className="has-margin-left-0 is-autostart-tag" isColor="yellow">
              {t('overview.startsAutomatically')}
            </Tag>
          )}
        </div>
        {/* Date chips below title on mobile (replace the hidden columns) */}
        {isMobile && (
          <Stack direction="row" sx={{ mt: 0.75, flexWrap: 'wrap', gap: '4px' }}>
            {x.start_date && (
              <Tag className="is-rounded is-light created-at-tag">
                {DateUtil.format(x.start_date, DateUtil.ShortFormat)}
              </Tag>
            )}
            {x.due_date && (
              <TableDueDate date={DateUtil.parse(x.due_date as string)} />
            )}
          </Stack>
        )}
      </div>
    )

    return {
      title: titleCell,
      latest_result: (
        <div>
          <Link
            to="#"
            onClick={(e) => {
              e.preventDefault()
              handleLatestResultClick(x.schedule_id)
            }}
            className="latest-result-link"
          >
            <Tooltip title={t('overview.latestResultLink')}>
              <i className="fas fa-arrow-up-right-from-square" />
            </Tooltip>
          </Link>
        </div>
      ),
      due_date: x.due_date,
      start_date: x.start_date,
      rawTitle: x.title,
      id: `${x.schedule_id}-${x.due_date}`,
      repeat: x.repeat,
      project: <div className="is-ellipsed action-table-project">{x.project_name ?? ''}</div>,
      rawProject: x.project_name ?? '',
      project_id: x.project_id
    }
  })

  const headers: IDataTableHeader[] = isMobile
    ? [{ field: 'title', label: t('resultSet.title'), width: '100%' }]
    : [
        { field: 'title', label: t('resultSet.title'), width: '30%' },
        { field: 'latest_result', label: t('resultSet.latestResult'), width: '10%' },
        { field: 'start_date', label: t('schedule.fields.startDate'), formatAs: 'dueDate', width: '10%' },
        { field: 'due_date', label: t('resultSet.dueShort'), formatAs: 'dueDate', width: '10%' },
        { field: 'repeat', label: 'Intervall', width: '10%', formatAs: 'repeat' },
        { field: 'project', label: 'Projekt', width: '20%' }
      ]

  if (newDesign) {
    return (
      <>
      <PlannedProcessDialog item={opened} startLink={startLink} onLatest={handleLatestResultClick} onClose={() => setOpened(null)} />
      <ScheduledListV2
        items={displayData}
        isLoading={isLoading}
        isPhone={isMobile}
        startLink={startLink}
        onLatest={handleLatestResultClick}
        onOpen={setOpened}
        sortColumn={sortSettings.column}
        sortDir={sortSettings.direction}
        onSort={(field, direction) => dispatch(setNextInspectionsSort({ column: field, direction }))}
        page={page}
        totalPages={isMobile ? 1 : desktopTotalPages}
        onPagination={setPage}
        sentinel={isMobile ? (
          <>
            <div ref={sentinelRef} style={{ height: 1 }} />
            {isLoading && page > 1 && <LoadSpinner className="centered" />}
          </>
        ) : null}
      />
      </>
    )
  }

  return (
    <div className={isMobile ? 'folder-mobile' : undefined}>
      <PlannedProcessDialog item={opened} startLink={startLink} onLatest={handleLatestResultClick} onClose={() => setOpened(null)} />
      {/* Initial-load spinner (no data yet) */}
      {isLoading && (isMobile ? mobileItems.length === 0 : true) && (
        <LoadSpinner className="centered" />
      )}
      <DataTable
        isLoading={isMobile ? mobileItems.length === 0 && isLoading : isLoading}
        data={tableData}
        actions={[]}
        headers={headers}
        currentPage={page}
        onPagination={(p) => setPage(p)}
        onSort={(field, direction) => dispatch(setNextInspectionsSort({ column: field, direction }))}
        sortColumn={sortSettings.column}
        sortDir={sortSettings.direction}
        totalPages={isMobile ? 1 : desktopTotalPages}
      />
      {isMobile && (
        <>
          <div ref={sentinelRef} style={{ height: 1 }} />
          {/* "Loading more" spinner while fetching the next page on scroll */}
          {isLoading && page > 1 && <LoadSpinner className="centered" />}
        </>
      )}
    </div>
  )
}
